import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Stack,
  Pagination,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  CertificateCriteriaListAction,
  DeleteCertificateCriteriaAction,
} from "../../Redux/Actions/RewardAction";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CreateCertificateCriteriaModal from "./CreateCertificateCriteriaModal";
import EditCertificateCriteriaModal from "./EditCertificateCriteriaModal";
import { ApiConfig } from "../../Config/CommonConfig";

interface CertificateCriteriaModalProps {
  open: boolean;
  onClose: () => void;
  certificate: any;
}

const CertificateCriteriaModal: React.FC<CertificateCriteriaModalProps> = ({
  open,
  onClose,
  certificate,
}) => {
  const dispatch = useDispatch<any>();
  const criteriaState = useSelector(
    (state: any) => state?.CertificateCriteriaListData
  );
  const criteriaData = criteriaState?.data || { rows: [], count: 0 };
  const loading = criteriaState?.loading || false;

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const filteredCriteria = Array.isArray(criteriaData?.rows)
    ? criteriaData?.rows?.filter((c: any) => c?.template_id === certificate?.id)
    : Array.isArray(criteriaData)
    ? criteriaData?.filter((c: any) => c?.template_id === certificate?.id)
    : [];

  const totalFilteredCount = filteredCriteria?.length;

  const paginatedCriteria = filteredCriteria?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const refreshCriteriaList = () => {
    const url = `${ApiConfig?.CertificateCriteriaList}`;
    dispatch(CertificateCriteriaListAction(url));
  };

  useEffect(() => {
    if (open) {
      refreshCriteriaList();
      setPage(1);
    }
  }, [dispatch, open]);

  const handleEdit = (criteria: any) => {
    setSelectedCriteria(criteria);
    setEditModalOpen(true);
  };

  const handleDelete = async (criteriaId: string) => {
    if (window.confirm("Are you sure you want to delete this criteria?")) {
      const result = await dispatch(DeleteCertificateCriteriaAction(criteriaId));
      if (result && result.status === true) {
        refreshCriteriaList(); 
      }
    }
  };

  const calculateTotalPages = (count: number, pageSize: number) => {
    return Math.ceil(count / pageSize);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Award Criteria for: {certificate?.name}</span>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            sx={{
              backgroundColor: "#0555c8",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#043c99",
              },
            }}
            size="small"
          >
            Create Criteria
          </Button>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#0555c8" }}>
                    <TableRow>
                      <TableCell sx={{ color: "white" }}>Name</TableCell>
                      <TableCell sx={{ color: "white" }}>Criteria Type</TableCell>
                      <TableCell sx={{ color: "white" }}>Level</TableCell>
                      <TableCell sx={{ color: "white" }}>Assessment ID</TableCell>
                      <TableCell sx={{ color: "white" }}>Status</TableCell>
                      <TableCell sx={{ color: "white" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCriteria?.length > 0 ? (
                      paginatedCriteria?.map((criteria: any) => {
                        const assessmentId =
                          criteria?.criteria_json?.[criteria?.criteria_type]?.[
                            criteria?.level
                          ]?.assessment_id || "-";

                        return (
                          <TableRow key={criteria?.id}>
                            <TableCell>{criteria?.name || "-"}</TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>
                              {criteria?.criteria_type}
                            </TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>
                              {criteria?.level}
                            </TableCell>
                            <TableCell>{assessmentId}</TableCell>
                            <TableCell>
                              <Chip
                                label={criteria?.is_active ? "Active" : "Inactive"}
                                color={criteria?.is_active ? "success" : "default"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(criteria)}
                                  sx={{ color: "#0555c8" }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(criteria?.id)}
                                  sx={{ color: "#d32f2f" }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No criteria found for this certificate.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalFilteredCount > rowsPerPage && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <Stack spacing={2}>
                    <Pagination
                      count={calculateTotalPages(totalFilteredCount, rowsPerPage)}
                      page={page}
                      onChange={(_, newPage) => setPage(newPage)}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Stack>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <CreateCertificateCriteriaModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        certificateId={certificate?.id}
        onSuccess={refreshCriteriaList}
      />

      {editModalOpen && selectedCriteria && (
        <EditCertificateCriteriaModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedCriteria(null);
          }}
          criteria={selectedCriteria}
          onSuccess={refreshCriteriaList}
        />
      )}
    </>
  );
};

export default CertificateCriteriaModal;
